import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ChannelInMarketResearchKeys,
  useChannelInMarketResearchQuery,
} from '@/queries/useChannelInMarketResearchQuery';
import { PDVInMarketResearchKeys } from '@/queries/usepdvInMarketResearchQuery';
import { saveChannelsInMarketResearch } from '@/api/business/marketResearch';
import { isAxiosError } from '@/api/business';
import { useToast } from '@/components/ui/use-toast';
import { Checkbox } from '@/components/ui/checkbox';
import Button from '@/components/Button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { TypographyMuted } from '@/components/ui/typography';
import LoadingScreen from '@/pages/LoadingScreen';
import { ChannelMarketResearch } from '@/types/MarketResearch';
import { CheckSquare, Filter, Layers, Save, Square } from 'lucide-react';

interface TableChannelsProps {
  id: number;
  status?: string;
}

const TableChannelsMarketResearch = ({ id, status }: TableChannelsProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data, isLoading } = useChannelInMarketResearchQuery(Number(id));

  const [selectedChannelIds, setSelectedChannelIds] = useState<number[]>([]);
  const [searchFilter, setSearchFilter] = useState('');
  const isDraft = status === 'Rascunho';

  useEffect(() => {
    if (data) {
      const initialSelected = data
        .filter((c) => Boolean(c.IS_SELECTED))
        .map((c) => c.SK_CANAL);
      setSelectedChannelIds(initialSelected);
    }
  }, [data]);

  const { mutate, isPending } = useMutation({
    mutationFn: saveChannelsInMarketResearch,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ChannelInMarketResearchKeys.detail(id),
      });
      queryClient.invalidateQueries({
        queryKey: PDVInMarketResearchKeys.detail(id),
      });
      toast({
        title: 'Canais salvos com sucesso',
        description:
          'Os PDVs da pesquisa foram atualizados de acordo com os canais selecionados.',
      });
    },
    onError: (err) => {
      let errorMessage =
        'Não foi possível salvar os canais. Tente novamente mais tarde.';
      if (isAxiosError(err)) {
        errorMessage = err.response?.data.message || errorMessage;
      }
      toast({
        title: 'Erro ao salvar canais',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  const channelsByResale = useMemo(() => {
    if (!data) return {};
    const filtered = data.filter((c) => {
      if (!searchFilter.trim()) return true;
      const term = searchFilter.toLowerCase();
      return (
        (c.DS_CANAL && c.DS_CANAL.toLowerCase().includes(term)) ||
        (c.NK_CODIGO_CANAL && c.NK_CODIGO_CANAL.toLowerCase().includes(term)) ||
        (c.DD_DIVISAO_CANAL &&
          c.DD_DIVISAO_CANAL.toLowerCase().includes(term)) ||
        (c.DS_REVENDA && c.DS_REVENDA.toLowerCase().includes(term))
      );
    });

    const grouped: Record<string, ChannelMarketResearch[]> = {};
    filtered.forEach((channel) => {
      const resaleName = channel.DS_REVENDA || 'Revenda Desconhecida';
      if (!grouped[resaleName]) {
        grouped[resaleName] = [];
      }
      grouped[resaleName].push(channel);
    });

    return grouped;
  }, [data, searchFilter]);

  const totalChannels = data?.length || 0;
  const selectedCount = selectedChannelIds.length;

  const handleToggleChannel = (skCanal: number) => {
    if (!isDraft) return;
    setSelectedChannelIds((prev) =>
      prev.includes(skCanal)
        ? prev.filter((id) => id !== skCanal)
        : [...prev, skCanal],
    );
  };

  const handleSelectResaleAll = (channelsInResale: ChannelMarketResearch[]) => {
    if (!isDraft) return;
    const idsInResale = channelsInResale.map((c) => c.SK_CANAL);
    setSelectedChannelIds((prev) =>
      Array.from(new Set([...prev, ...idsInResale])),
    );
  };

  const handleDeselectResaleAll = (
    channelsInResale: ChannelMarketResearch[],
  ) => {
    if (!isDraft) return;
    const idsInResale = new Set(channelsInResale.map((c) => c.SK_CANAL));
    setSelectedChannelIds((prev) => prev.filter((id) => !idsInResale.has(id)));
  };

  const handleSelectAllGlobal = () => {
    if (!isDraft || !data) return;
    setSelectedChannelIds(data.map((c) => c.SK_CANAL));
  };

  const handleDeselectAllGlobal = () => {
    if (!isDraft) return;
    setSelectedChannelIds([]);
  };

  const handleSave = () => {
    mutate({
      SK_PESQUISA: id,
      SK_CANAIS: selectedChannelIds,
    });
  };

  if (isLoading) return <LoadingScreen />;

  return (
    <div className="flex flex-col gap-4 p-2 bg-white rounded-lg border border-gray-100 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pb-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Layers className="w-5 h-5 text-primary" />
          <div>
            <h4 className="text-sm font-semibold text-gray-900">
              Canais de Atendimento
            </h4>
            <TypographyMuted className="text-xs">
              Selecione os canais participantes de cada revenda ({selectedCount}{' '}
              de {totalChannels} selecionados)
            </TypographyMuted>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isDraft && (
            <>
              <Button
                variant="outline"
                size="sm"
                className="text-xs h-8"
                onClick={handleSelectAllGlobal}
              >
                <CheckSquare className="w-3.5 h-3.5 mr-1" />
                Marcar Todos
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-xs h-8"
                onClick={handleDeselectAllGlobal}
              >
                <Square className="w-3.5 h-3.5 mr-1" />
                Desmarcar Todos
              </Button>
              <Button
                size="sm"
                className="text-xs h-8 gap-1.5"
                onClick={handleSave}
                disabled={isPending}
              >
                <Save className="w-3.5 h-3.5" />
                {isPending ? 'Salvando...' : 'Salvar Canais'}
              </Button>
            </>
          )}
          {!isDraft && (
            <Badge variant="secondary" className="text-xs">
              Somente leitura (Pesquisa {status})
            </Badge>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Filter className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Filtrar por canal, código ou divisão..."
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            className="pl-9 text-xs h-9"
          />
        </div>
      </div>

      {Object.keys(channelsByResale).length === 0 ? (
        <div className="py-8 text-center text-sm text-gray-500">
          Nenhum canal encontrado para as revendas selecionadas nesta pesquisa.
        </div>
      ) : (
        <div className="flex flex-col gap-4 max-h-[380px] overflow-y-auto pr-1">
          {Object.entries(channelsByResale).map(([resaleName, channels]) => {
            const resaleSelectedCount = channels.filter((c) =>
              selectedChannelIds.includes(c.SK_CANAL),
            ).length;

            return (
              <div
                key={resaleName}
                className="flex flex-col gap-2 p-3 bg-gray-50/70 rounded-md border border-gray-200/60"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-xs text-gray-800">
                      {resaleName}
                    </span>
                    <Badge variant="outline" className="text-[10px] bg-white">
                      {resaleSelectedCount} / {channels.length} selecionados
                    </Badge>
                  </div>
                  {isDraft && (
                    <div className="flex items-center gap-2 text-xs">
                      <button
                        type="button"
                        onClick={() => handleSelectResaleAll(channels)}
                        className="text-primary hover:underline font-medium text-[11px]"
                      >
                        Marcar revenda
                      </button>
                      <span className="text-gray-300">|</span>
                      <button
                        type="button"
                        onClick={() => handleDeselectResaleAll(channels)}
                        className="text-gray-500 hover:underline font-medium text-[11px]"
                      >
                        Desmarcar revenda
                      </button>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mt-1">
                  {channels.map((channel) => {
                    const isChecked = selectedChannelIds.includes(
                      channel.SK_CANAL,
                    );
                    return (
                      <div
                        key={channel.SK_CANAL}
                        onClick={() =>
                          isDraft && handleToggleChannel(channel.SK_CANAL)
                        }
                        className={`flex items-start gap-2.5 p-2 rounded border text-xs cursor-pointer transition-colors ${
                          isChecked
                            ? 'bg-primary/5 border-primary/30 text-gray-900 font-medium'
                            : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                        } ${!isDraft ? 'cursor-default opacity-80' : ''}`}
                      >
                        <Checkbox
                          checked={isChecked}
                          onCheckedChange={() =>
                            handleToggleChannel(channel.SK_CANAL)
                          }
                          disabled={!isDraft}
                          className="mt-0.5"
                        />
                        <div className="flex flex-col min-w-0">
                          <span className="truncate">
                            {channel.DS_CANAL ||
                              `Canal ${channel.NK_CODIGO_CANAL}`}
                          </span>
                          {(channel.NK_CODIGO_CANAL ||
                            channel.DD_DIVISAO_CANAL) && (
                            <span className="text-[10px] text-gray-400 truncate">
                              {channel.NK_CODIGO_CANAL &&
                                `Cód: ${channel.NK_CODIGO_CANAL}`}
                              {channel.NK_CODIGO_CANAL &&
                                channel.DD_DIVISAO_CANAL &&
                                ' • '}
                              {channel.DD_DIVISAO_CANAL &&
                                `Div: ${channel.DD_DIVISAO_CANAL}`}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TableChannelsMarketResearch;
